import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Flex, Group, Stack, Text, Title } from '@mantine/core';

import { useAppDispatch } from '@/store/hooks';
import { patientsApiSlice } from '@/store/slices/api';
import {
  useUpdateEriTrackingMutation
} from '@/store/slices/api/updateEriTrackingApiSlice';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import styles from '../DeviceScheduleModal.module.scss';
import { EditScheduleStepProps } from '../types';

const CancelERITracking: React.FC<EditScheduleStepProps> = ({
  onBack,
  onClose,
  loading,
  setLoading,
  deviceInfo
}) => {
  const { id: patientId } = useParams() as { id: string };
  const [showSummary, setShowSummary] = useState(false);
  const dispatch = useAppDispatch();
  const [updateEriTracking] = useUpdateEriTrackingMutation();

  const handleConfirm = async () => {
    if (!deviceInfo) {
      return;
    }

    setLoading(true);
    try {
      await updateEriTracking({
        deviceId: deviceInfo.deviceId,
        isEriTracked: false
      }).unwrap();

      dispatch(patientsApiSlice.util.invalidateTags([{ type: API_SLICE.Patient, id: patientId }]));

      setShowSummary(true);
    } catch (error) {
      console.error('Error cancelling ERI tracking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowSummary(false);
    onClose();
  };

  if (showSummary) {
    return (
      <Stack gap={'xs'}>
        <Flex px={20} py={16} direction={'column'} gap={4}>
          <Title order={5} fw={600} c={'plum.9'}>
            ERI Tracking Cancelled Successfully
          </Title>
          <Text size="sm" c={'plum'}>
            ERI tracking has been successfully cancelled and removed from the device.
          </Text>
        </Flex>
        <Group className={styles.buttonGroup} justify="flex-end">
          <Button color="plum.9" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap={'xs'}>
      <Flex px={20} py={16} direction={'column'} gap={4}>
        <Title order={5} fw={600} c={'plum.9'}>
          Cancel ERI Tracking
        </Title>
        <Text size="sm" c={'plum'}>
          This will cancel the ERI Tracking schedule and remove ERI Tracking from the device. Please
          note that this action cannot be undone.
        </Text>
      </Flex>

      <Group className={styles.buttonGroup} justify="flex-end">
        <Button
          variant="outline"
          color="plum.9"
          onClick={onBack}
          disabled={loading}
          className={styles.modalButton}>
          Back
        </Button>
        <Button
          color="plum.9"
          onClick={handleConfirm}
          loading={loading}
          className={styles.modalButton}>
          Confirm
        </Button>
      </Group>
    </Stack>
  );
};

export default CancelERITracking;
